import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./ExerciseDemoModal.module.css";
import ModalHeader from "./ModalHeader";
import { getMediaForExerciseName } from "../data/exerciseMediaMap";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

function resolveUrl(path) {
  if (!path) return null;
  const p = String(path).trim();
  if (/^https?:\/\//i.test(p)) return p;
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const rel = p.replace(/^\/+/, "");
  return `${base}/${rel}`;
}

function getYouTubeId(url){
  if (!url) return null;
  try{
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) return u.pathname.replace(/^\//,'');
    if (u.hostname.includes('youtube.com')){
      if (u.pathname.startsWith('/watch')) return u.searchParams.get('v');
      if (u.pathname.startsWith('/shorts/')) return u.pathname.split('/')[2];
      if (u.pathname.startsWith('/embed/')) return u.pathname.split('/')[2];
    }
  }catch(_){}
  return null;
}


/**
 * Embed do YouTube com reforço de autoplay via postMessage.
 *
 * O parâmetro autoplay=1&mute=1 na URL costuma bastar, mas navegadores com
 * proteção de rastreamento mais estrita (Firefox Enhanced Tracking
 * Protection, alguns bloqueadores de anúncio/privacidade) podem ignorá-lo
 * silenciosamente — o vídeo carrega parado e sem nenhum aviso de erro.
 * enablejsapi=1 habilita o iframe a aceitar comandos via postMessage
 * (protocolo da IFrame Player API do YouTube); manda o comando "play"
 * explícito algumas vezes logo após o load, como reforço — não depende de
 * carregar o script iframe_api completo, só do protocolo de mensagens.
 */
function YouTubeEmbed({ ytId }){
  const iframeRef = useRef(null);

  const sendPlay = () => {
    try {
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "playVideo", args: [] }),
        "*"
      );
    } catch (_) {}
  };

  useEffect(() => {
    const timers = [300, 900, 1800].map((ms) => setTimeout(sendPlay, ms));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ytId]);

  const src = `https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&mute=1&loop=1&playlist=${ytId}&modestbranding=1&controls=0&rel=0&playsinline=1&enablejsapi=1`;

  return (
    <div className={styles.videoWrap}>
      <iframe
        ref={iframeRef}
        src={src}
        title="YouTube demo"
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        onLoad={sendPlay}
      />
    </div>
  );
}

function getGoogleDriveId(url){
  if (!url) return null;
  try{
    const u = new URL(url);
    if (!u.hostname.includes('drive.google.com')) return null;
    let id = null;
    if (u.pathname.startsWith('/file/d/')){
      const parts = u.pathname.split('/');
      if (parts[3]) id = parts[3];
    }
    if (!id) id = u.searchParams.get('id');
    return id || null;
  }catch(_){}
  return null;
}

export default function ExerciseDemoModal({ open, onClose, exerciseItem }){
  const fallbackMedia = exerciseItem?.name ? getMediaForExerciseName(exerciseItem.name) : null;
  const shellRef = useRef(null);

  // R242/R243 corrigiram pontos concretos (stopPropagation faltando no bloco
  // de grupo; isolamento do header), mas o problema voltou (relato real de
  // treino, 21-22/09/2026): qualquer clique no card durante o treino ativo
  // acaba abrindo/reabrindo o vídeo, o X para de responder, e só um restart
  // completo do app resolve. Isso não é bubbling de evento (já corrigido) —
  // é o WKWebView errando o HIT-TEST depois que o <video> autoplay começa,
  // um bug conhecido de compositing do Safari/iOS em modo standalone. Duas
  // camadas de mitigação, já que não dá pra reproduzir localmente (sem
  // device iOS) pra confirmar isoladamente:
  //   1) CSS: promover o modal inteiro (.overlay) e o vídeo (.videoWrap) pra
  //      suas próprias camadas de composição DESDE O INÍCIO (isolation +
  //      translateZ) — antes só o header tinha isso; a fonte do autoplay
  //      (o próprio vídeo) nunca tinha sido isolada.
  //   2) JS: força um "nudge" de repaint (toggle de transform em 2 frames)
  //      assim que o vídeo dispara "playing" — imita, em miniatura, o
  //      reflow que sair/voltar do app força (o que hoje é o único jeito
  //      relatado de destravar).
  function nudgeRepaintAfterPlay() {
    const el = shellRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.style.transform = "translateZ(0.001px)";
      requestAnimationFrame(() => {
        el.style.transform = "translateZ(0)";
      });
    });
  }

  // Achado de revisão: em vez de deixar o <video> travado/quebrado quando o
  // navegador não consegue decodificar o arquivo (ex: .mov com codec HEVC
  // em navegador sem suporte), cai pro mesmo placeholder do "sem mídia" —
  // sensível especialmente pro aluno self-service, que não tem professor
  // pra avisar que a demo tá quebrada. Reseta a cada exercício/abertura
  // diferente, senão um erro de um vídeo "vazaria" pro próximo.
  const [videoError, setVideoError] = useState(false);
  useEffect(() => {
    setVideoError(false);
  }, [open, exerciseItem?.id, exerciseItem?.demo_url]);

  useBodyScrollLock(!!open);
  if (!open) return null;

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.shell} ref={shellRef} onClick={(e)=>e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={styles.headerLayer}>
          <ModalHeader onClose={onClose} />
        </div>
        <div className={styles.header}>
          <div className={styles.title}>{exerciseItem?.name || "Exercício"}</div>
        </div>

        <div className={styles.body}>
          <div className={styles.panelFull}>
            <div className={styles.demoStage}>
              {(() => {
                const raw = exerciseItem?.demo_url || fallbackMedia;
                const ytId = getYouTubeId(raw || '');
                if (ytId){
                  return <YouTubeEmbed ytId={ytId} />;
                }

                const gdId = getGoogleDriveId(raw || '');
                if (gdId){
                  const previewSrc = `https://drive.google.com/file/d/${gdId}/preview`;
                  return (
                    <div className={styles.videoWrap}>
                      <iframe src={previewSrc} title="Google Drive video" allow="autoplay; encrypted-media; fullscreen" />
                    </div>
                  );
                }

                const url = resolveUrl(raw);
                // Achado de revisão: extensão inclui .mov (QuickTime, padrão
                // do iPhone/Mac) — antes só reconhecia mp4/webm/ogg e um
                // vídeo .mov caía no branch de <img> logo abaixo, quebrando
                // visualmente a demo pro aluno (nunca decodifica QuickTime
                // como imagem).
                const isVideoFile = url && /\.(mp4|webm|ogg|mov)$/i.test(url);
                if (isVideoFile && !videoError) {
                  return (
                    <div className={styles.videoWrap}>
                      {/* controls={false} some com a barra de controle nativa. Não trava
                          interação (pointerEvents/overlay bloqueador removidos) pelo mesmo
                          motivo do bloco do YouTube acima: se o autoplay não disparar por
                          qualquer razão, o usuário precisa conseguir clicar pra tocar. */}
                      <video
                        src={url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        webkit-playsinline="true"
                        controls={false}
                        controlsList="nodownload noplaybackrate noremoteplayback"
                        disablePictureInPicture
                        onContextMenu={(e) => e.preventDefault()}
                        onClick={(e) => {
                          const v = e.currentTarget;
                          if (v.paused) v.play(); else v.pause();
                        }}
                        onPlaying={nudgeRepaintAfterPlay}
                        onError={() => setVideoError(true)}
                      />
                    </div>
                  );
                }

                // Arquivo é vídeo mas não tocou (codec não suportado pelo
                // navegador, arquivo corrompido, etc.) — mesmo placeholder
                // do "sem mídia" abaixo, com um aviso curto, em vez de deixar
                // a tela preta/quebrada sem explicação nenhuma.
                if (isVideoFile && videoError) {
                  return (
                    <>
                      <div className={styles.figure} aria-hidden>
                        <div className={styles.bar} />
                        <div className={styles.limb} style={{ left: 108, top: 34, height: 70, transform: "rotate(6deg)" }} />
                        <div className={styles.limb} style={{ left: 114, top: 98, height: 56, transform: "rotate(-6deg)" }} />
                        <div className={styles.limb} style={{ left: 70, top: 52, height: 52, transform: "rotate(50deg)" }} />
                        <div className={styles.limb} style={{ left: 156, top: 52, height: 52, transform: "rotate(-50deg)" }} />
                        <div className={styles.joint} style={{ left: 108, top: 30 }} />
                        <div className={styles.joint} style={{ left: 86, top: 62 }} />
                        <div className={styles.joint} style={{ left: 174, top: 62 }} />
                        <div className={styles.joint} style={{ left: 114, top: 96 }} />
                      </div>
                      <p className={styles.hint}>Não foi possível carregar o vídeo de demonstração neste dispositivo.</p>
                    </>
                  );
                }

                if (url) {
                  return (
                    <img src={url} alt="Demonstração" loading="lazy" style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 12 }} />
                  );
                }

                return (
                  <>
                    <div className={styles.figure} aria-hidden>
                      <div className={styles.bar} />
                      <div className={styles.limb} style={{ left: 108, top: 34, height: 70, transform: "rotate(6deg)" }} />
                      <div className={styles.limb} style={{ left: 114, top: 98, height: 56, transform: "rotate(-6deg)" }} />
                      <div className={styles.limb} style={{ left: 70, top: 52, height: 52, transform: "rotate(50deg)" }} />
                      <div className={styles.limb} style={{ left: 156, top: 52, height: 52, transform: "rotate(-50deg)" }} />
                      <div className={styles.joint} style={{ left: 108, top: 30 }} />
                      <div className={styles.joint} style={{ left: 86, top: 62 }} />
                      <div className={styles.joint} style={{ left: 174, top: 62 }} />
                      <div className={styles.joint} style={{ left: 114, top: 96 }} />
                    </div>
                    <div className={styles.repBar}><div className={styles.repDot} /></div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>,
    document.body
  );
}